import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner, StatusBadge, Tab, Tabs } from '@/components/ui';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import ResponsiveModal from '../../../components/ResponsiveModal';
import {
  fetchPcrFilterOptions,
  formatPcrRoadAssetLabel,
  formatPcrServiceAreaLabel,
  formatPcrStatusLabel,
  getPcrSubmissionImageUrl,
  patchPcrSubmissionStatus,
  uploadPcrSubmissionImage,
  normalizePcrReportTypeBucket,
  PCR_FILTER_SERVICE_AREAS,
  PCR_STATUS_OPTIONS,
  type PcrFilterAssetOption,
  type PcrForemanAreaOption,
  type PcrStatus,
  type PcrSubmission,
} from '../../../services/pcrService';
import { assetService } from '../../../services/assetService';

function formatIso(value: string | null): string {
  if (!value) return '–';
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return value;
  }
}

/** Matches Work ID detail modal read-only fields */
const readonlyBoxLight = 'tw-bg-surface-alt tw-border tw-border-line tw-rounded tw-px-2 tw-py-2';
const editableBoxWhite = 'tw-bg-surface tw-border tw-border-line tw-rounded tw-px-2 tw-py-2';

/** Row count for read-only message textarea */
function readOnlyMessageRows(text: string | null | undefined): number {
  if (text == null || text === '') return 1;
  const explicitLines = text.split('\n').length;
  const wrappedApprox = Math.ceil(text.length / 100);
  return Math.min(18, Math.max(1, Math.max(explicitLines, wrappedApprox)));
}

interface PcrSubmissionDetailModalProps {
  isOpen: boolean;
  submission: PcrSubmission | null;
  /** Intake fields, foreman/road/requester/message, photo replace (submission:pcr_update | workId:create). */
  canPatchPcrSubmission?: boolean;
  /** Set workflow to Internal Review (pcr:internal_review, or implied by canPatchPcrSubmission). */
  canSetInternalReview?: boolean;
  /** When false, users cannot choose Finalized in the status dropdown (unless already finalized). */
  canFinalizePcr?: boolean;
  /** Runs after successful save or status PATCH (reload list/detail). */
  onSubmissionUpdated?: () => Promise<void>;
  startInEditMode?: boolean;
  onClose: () => void;
  onSaveSubmission?: (input: {
    status: PcrStatus;
    service_area_text?: string;
    community_text?: string;
    foremanAreaId?: number;
    roadAssetId?: number;
    report_type?: string;
    activity_type?: string;
    user_name?: string;
    user_email?: string;
    user_phone?: string;
    contact_method?: string;
    message?: string;
    is_feedback?: boolean;
    noContactNote?: string | null;
  }) => Promise<void>;
  onOpenLinkedWorkId?: (row: PcrSubmission) => void;
}

const PcrSubmissionDetailModal: React.FC<PcrSubmissionDetailModalProps> = ({
  isOpen,
  submission,
  canPatchPcrSubmission = false,
  canSetInternalReview = false,
  canFinalizePcr = false,
  onSubmissionUpdated,
  startInEditMode = false,
  onClose,
  onSaveSubmission,
  onOpenLinkedWorkId,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [statusDraft, setStatusDraft] = useState<PcrStatus>('new');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'photos' | 'work-id'>('details');
  const [wrapPhotoUrl, setWrapPhotoUrl] = useState<string | null>(null);
  const [wrapPhotoLoading, setWrapPhotoLoading] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingPhotoObjectUrl, setPendingPhotoObjectUrl] = useState<string | null>(null);
  const [serviceAreaIdDraft, setServiceAreaIdDraft] = useState<string>('');
  const [communityDraft, setCommunityDraft] = useState('');
  const [roadAssetIdDraft, setRoadAssetIdDraft] = useState<number | ''>('');
  const [reportTypeDraft, setReportTypeDraft] = useState('');
  const [activityTypeDraft, setActivityTypeDraft] = useState('');
  const [requesterDraft, setRequesterDraft] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  const [contactMethodDraft, setContactMethodDraft] = useState('');
  const [feedbackDraft, setFeedbackDraft] = useState(false);
  const [noContactNoteDraft, setNoContactNoteDraft] = useState('');
  const [messageDraft, setMessageDraft] = useState('');
  const [foremanAreaIdDraft, setForemanAreaIdDraft] = useState<number | ''>('');
  const [foremanAreaOptions, setForemanAreaOptions] = useState<PcrForemanAreaOption[]>([]);
  const [roadOptions, setRoadOptions] = useState<PcrFilterAssetOption[]>([]);
  const [roadsLoading, setRoadsLoading] = useState(false);

  const parseServiceAreaId = (raw: string | null | undefined): string | null => {
    const value = String(raw || '').trim();
    const match = value.match(/\b(16|17|22|25)\b/);
    if (match) return match[1];
    const v = value.toLowerCase();
    if (v.includes('south') && v.includes('cariboo')) return '16';
    if (v.includes('central') && v.includes('cariboo')) return '17';
    if (v.includes('north') && v.includes('peace')) return '22';
    if (v.includes('bulkley') && (v.includes('nass') || v.includes('nechako') || v.includes('nichako'))) return '25';
    return null;
  };

  useEffect(() => {
    if (!isOpen || !submission) return;
    setEditMode(startInEditMode);
    setStatusDraft(submission.status || 'new');
    setSaving(false);
    setError(null);
    setActiveTab('details');
    setServiceAreaIdDraft(parseServiceAreaId(submission.service_area_text) || '');
    setCommunityDraft(submission.community_text || '');
    setRoadAssetIdDraft('');
    setReportTypeDraft(submission.report_type || '');
    setActivityTypeDraft(submission.activity_type || '');
    setRequesterDraft(submission.user_name || '');
    setEmailDraft(submission.user_email || '');
    setPhoneDraft(submission.user_phone || '');
    setContactMethodDraft(submission.contact_method || '');
    setFeedbackDraft(Boolean(submission.is_feedback));
    setNoContactNoteDraft(submission.noContactNote || '');
    setMessageDraft(submission.message || '');
    setForemanAreaIdDraft('');
    setForemanAreaOptions([]);
    setRoadOptions([]);
    setPendingImageFile(null);
    const serviceAreaId = parseServiceAreaId(submission.service_area_text);
    if (!serviceAreaId) return;
    void (async () => {
      try {
        const options = await fetchPcrFilterOptions(serviceAreaId);
        setForemanAreaOptions(options.foremanAreas ?? []);
        const normalizedForeman = (submission.foreman_area_name ?? submission.community_text ?? '')
          .trim()
          .toLowerCase();
        const foremanMatch = (options.foremanAreas ?? []).find(
          (f) => (f.name || '').trim().toLowerCase() === normalizedForeman,
        );
        if (foremanMatch) setForemanAreaIdDraft(foremanMatch.id);
      } catch {
        setForemanAreaOptions([]);
      }
    })();
  }, [isOpen, startInEditMode, submission]);

  useEffect(() => {
    if (!isOpen || !editMode || !serviceAreaIdDraft) return;
    void (async () => {
      try {
        const options = await fetchPcrFilterOptions(serviceAreaIdDraft);
        setForemanAreaOptions(options.foremanAreas ?? []);
      } catch {
        setForemanAreaOptions([]);
      }
    })();
  }, [isOpen, editMode, serviceAreaIdDraft]);

  useEffect(() => {
    if (!isOpen || !editMode) return;
    if (foremanAreaIdDraft === '') {
      setRoadOptions([]);
      setRoadAssetIdDraft('');
      return;
    }
    void (async () => {
      try {
        setRoadsLoading(true);
        const rows = await assetService.searchAssets({
          foremanAreaId: Number(foremanAreaIdDraft),
          assetType: 'road',
        });
        const options = (Array.isArray(rows) ? rows : []).map((r) => ({
          id: r.id,
          name: r.name ?? '',
          assetNumber: r.assetNumber ?? '',
        }));
        setRoadOptions(options);
        const currentRoad = (submission?.road_text || '').trim().toLowerCase();
        const match = options.find((a) => {
          const label = formatPcrRoadAssetLabel(a).toLowerCase();
          return (
            label === currentRoad ||
            (a.name || '').trim().toLowerCase() === currentRoad ||
            (a.assetNumber || '').trim().toLowerCase() === currentRoad
          );
        });
        if (match) setRoadAssetIdDraft(match.id);
      } finally {
        setRoadsLoading(false);
      }
    })();
  }, [isOpen, editMode, foremanAreaIdDraft, submission?.road_text]);

  const statusSelectOptions = useMemo(() => {
    const cur = submission?.status ?? 'new';
    let opts = [...PCR_STATUS_OPTIONS];
    if (!canFinalizePcr) {
      opts = opts.filter((o) => o.id !== 'finalized' || statusDraft === 'finalized');
    }
    if (!canSetInternalReview) {
      opts = opts.filter(
        (o) => o.id !== 'internal_review' || cur === 'internal_review' || statusDraft === 'internal_review',
      );
    }
    if (!canPatchPcrSubmission) {
      opts = opts.filter((o) => o.id !== 'new' || cur === 'new' || statusDraft === 'new');
    }
    return opts;
  }, [
    canFinalizePcr,
    canSetInternalReview,
    canPatchPcrSubmission,
    statusDraft,
    submission?.status,
  ]);

  /** Editable intake / photo sections (status uses workflow edit below). */
  const fieldsEditable = editMode && canPatchPcrSubmission;
  const workflowStatusEditable =
    editMode && (canPatchPcrSubmission || canSetInternalReview || canFinalizePcr);
  const canEnterEditMode = canPatchPcrSubmission || canSetInternalReview || canFinalizePcr;
  const canSaveStatusWorkflowOnly =
    !canPatchPcrSubmission &&
    Boolean(onSubmissionUpdated) &&
    (canSetInternalReview || canFinalizePcr);

  useEffect(() => {
    let objectUrl: string | null = null;
    if (pendingImageFile) {
      objectUrl = URL.createObjectURL(pendingImageFile);
      setPendingPhotoObjectUrl(objectUrl);
    } else {
      setPendingPhotoObjectUrl(null);
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [pendingImageFile]);

  useEffect(() => {
    if (!isOpen || !submission?.id) {
      setWrapPhotoUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setWrapPhotoLoading(false);
      return;
    }

    let alive = true;
    setWrapPhotoLoading(true);
    fetch(getPcrSubmissionImageUrl(submission.id), { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('no image');
        return res.blob();
      })
      .then((blob) => {
        if (!alive) return;
        const u = URL.createObjectURL(blob);
        setWrapPhotoUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return u;
        });
        setWrapPhotoLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setWrapPhotoUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setWrapPhotoLoading(false);
      });

    return () => {
      alive = false;
      setWrapPhotoUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setWrapPhotoLoading(false);
    };
  }, [isOpen, submission?.id, submission?.wrap_image_url, submission?.image_url]);

  if (!isOpen || !submission) return null;

  const currentStatus = (submission.status || 'new') as PcrStatus;
  const showMoveToInternalReview =
    !editMode && currentStatus === 'new' && canSetInternalReview && Boolean(onSubmissionUpdated);
  const showMoveToFinalize =
    !editMode &&
    currentStatus === 'internal_review' &&
    canFinalizePcr &&
    !submission.workIdId &&
    Boolean(onSubmissionUpdated);

  const moveToStatus = async (status: PcrStatus) => {
    setSaving(true);
    setError(null);
    try {
      await patchPcrSubmissionStatus(submission.id, { status });
      await onSubmissionUpdated?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update status.');
    } finally {
      setSaving(false);
    }
  };

  const saveStatus = async () => {
    if (!submission) return;
    setSaving(true);
    setError(null);
    try {
      if (pendingImageFile && !canPatchPcrSubmission) {
        setError('Uploading a photo requires permission to edit PCR submission fields.');
        return;
      }
      if (canSaveStatusWorkflowOnly && statusDraft !== submission.status) {
        await patchPcrSubmissionStatus(submission.id, { status: statusDraft });
        await onSubmissionUpdated?.();
        setEditMode(false);
        onClose();
        return;
      }
      if (!onSaveSubmission) {
        setError('Save is not available for this view.');
        return;
      }
      if (pendingImageFile) {
        await uploadPcrSubmissionImage(submission.id, pendingImageFile);
        setPendingImageFile(null);
      }
      await onSaveSubmission({
        status: statusDraft,
        service_area_text: serviceAreaIdDraft ? `Service Area ${serviceAreaIdDraft}` : undefined,
        community_text: communityDraft,
        foremanAreaId: foremanAreaIdDraft === '' ? undefined : foremanAreaIdDraft,
        roadAssetId: roadAssetIdDraft === '' ? undefined : roadAssetIdDraft,
        report_type: reportTypeDraft,
        activity_type: activityTypeDraft,
        user_name: requesterDraft,
        user_email: emailDraft,
        user_phone: phoneDraft,
        contact_method: contactMethodDraft,
        message: messageDraft,
        is_feedback: feedbackDraft,
        noContactNote: noContactNoteDraft || null,
      });
      await onSubmissionUpdated?.();
      setEditMode(false);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update status.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={`PCR Details${submission.report_id ? ` – ${submission.report_id}` : ''}`}
      // V2-074 / EDIT-01: until Edit is chosen, the details read as label / value text.
      readOnly={!editMode}
      size="large"
      footer={
        <>
          {showMoveToInternalReview ? (
            <Button
              variant="outline-info"
              disabled={saving}
              onClick={() => void moveToStatus('internal_review')}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="tw-mr-2" />
                  Moving…
                </>
              ) : (
                'Move to Internal Review'
              )}
            </Button>
          ) : null}
          {showMoveToFinalize ? (
            <Button
              variant="outline-success"
              disabled={saving}
              onClick={() => void moveToStatus('finalized')}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="tw-mr-2" />
                  Moving…
                </>
              ) : (
                'Move to Finalized'
              )}
            </Button>
          ) : null}
          {canEnterEditMode && !editMode && (onSaveSubmission || onSubmissionUpdated) && (
            <Button variant="outline-primary" onClick={() => setEditMode(true)} disabled={saving}>
              Edit
            </Button>
          )}
          {editMode ? (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setPendingImageFile(null);
                  setEditMode(false);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void saveStatus()}
                disabled={
                  saving ||
                  (canPatchPcrSubmission && !onSaveSubmission) ||
                  (!canPatchPcrSubmission && !canSaveStatusWorkflowOnly && !onSaveSubmission) ||
                  (Boolean(canSaveStatusWorkflowOnly) && statusDraft === submission.status)
                }
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="tw-mr-2" />
                    Saving…
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          )}
        </>
      }
    >
      {error && <Alert variant="danger">{error}</Alert>}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab((k as 'details' | 'photos' | 'work-id') || 'details')}
        className="tw-mb-3"
      >
        <Tab eventKey="details" title="Details">
          <div className="tw-py-2">
          <div className="tw-flex tw-flex-wrap -tw-mx-1">
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">PCR ID</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  className={readonlyBoxLight}
                  value={submission.report_id || '–'}
                />
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Status</Form.Label>
                {workflowStatusEditable ? (
                  <Form.Select
                    size="sm"
                    className={editableBoxWhite}
                    value={statusDraft}
                    onChange={(e) => setStatusDraft(e.target.value as PcrStatus)}
                  >
                    {statusSelectOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <div className={readonlyBoxLight}>
                    <StatusBadge
                      status={submission.status || 'new'}
                      vocabulary="pcr"
                      label={formatPcrStatusLabel(submission.status || 'new')}
                    />
                  </div>
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Received</Form.Label>
                <Form.Control type="text" readOnly className={readonlyBoxLight} value={formatIso(submission.receivedAt)} />
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Responded</Form.Label>
                <Form.Control type="text" readOnly className={readonlyBoxLight} value={formatIso(submission.respondedAt)} />
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Response SLA</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  className={readonlyBoxLight}
                  value={submission.responseTimeBucket || '–'}
                />
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Service Area</Form.Label>
                {fieldsEditable ? (
                  <Form.Select
                    size="sm"
                    className={editableBoxWhite}
                    value={serviceAreaIdDraft}
                    onChange={(e) => {
                      setServiceAreaIdDraft(e.target.value);
                      setForemanAreaIdDraft('');
                      setRoadAssetIdDraft('');
                    }}
                  >
                    <option value="">Select service area</option>
                    {PCR_FILTER_SERVICE_AREAS.map((sa) => (
                      <option key={sa.id} value={String(sa.id)}>
                        {sa.name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="text"
                    readOnly
                    className={readonlyBoxLight}
                    value={submission.service_area_text ? formatPcrServiceAreaLabel(submission.service_area_text) : '–'}
                  />
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Foreman Area</Form.Label>
                {fieldsEditable ? (
                  <Form.Select
                    size="sm"
                    className={editableBoxWhite}
                    value={foremanAreaIdDraft === '' ? '' : String(foremanAreaIdDraft)}
                    onChange={(e) => {
                      const next = e.target.value ? Number(e.target.value) : '';
                      setForemanAreaIdDraft(next);
                      const selected = foremanAreaOptions.find((f) => f.id === Number(next));
                      if (selected) setCommunityDraft(selected.name);
                      setRoadAssetIdDraft('');
                    }}
                  >
                    <option value="">Select foreman area</option>
                    {foremanAreaOptions.map((fa) => (
                      <option key={fa.id} value={fa.id}>
                        {fa.name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="text"
                    readOnly
                    className={readonlyBoxLight}
                    value={(submission.foreman_area_name ?? submission.community_text) || '–'}
                  />
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Road Asset</Form.Label>
                {fieldsEditable ? (
                  <SearchableDropdown<PcrFilterAssetOption>
                    options={roadOptions}
                    value={roadAssetIdDraft === '' ? '' : String(roadAssetIdDraft)}
                    onChange={(value) => setRoadAssetIdDraft(value ? Number(value) : '')}
                    formatOption={(road) => formatPcrRoadAssetLabel(road)}
                    getValue={(road) => road.id}
                    disabled={roadsLoading || foremanAreaIdDraft === ''}
                    placeholder={roadsLoading ? 'Loading roads…' : 'Select road'}
                    noOptionsMessage="No roads found for selected foreman area"
                  />
                ) : (
                  <Form.Control
                    type="text"
                    readOnly
                    className={readonlyBoxLight}
                    value={submission.road_text || '–'}
                  />
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Report Type</Form.Label>
                {fieldsEditable ? (
                  <Form.Control
                    type="text"
                    className={editableBoxWhite}
                    value={reportTypeDraft}
                    onChange={(e) => setReportTypeDraft(e.target.value)}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    readOnly
                    className={readonlyBoxLight}
                    value={normalizePcrReportTypeBucket(submission.report_type)}
                  />
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Activity</Form.Label>
                {fieldsEditable ? (
                  <Form.Control
                    type="text"
                    className={editableBoxWhite}
                    value={activityTypeDraft}
                    onChange={(e) => setActivityTypeDraft(e.target.value)}
                  />
                ) : (
                  <Form.Control type="text" readOnly className={readonlyBoxLight} value={submission.activity_type || '–'} />
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Requester</Form.Label>
                {fieldsEditable ? (
                  <Form.Control
                    type="text"
                    className={editableBoxWhite}
                    value={requesterDraft}
                    onChange={(e) => setRequesterDraft(e.target.value)}
                  />
                ) : (
                  <Form.Control type="text" readOnly className={readonlyBoxLight} value={submission.user_name || '–'} />
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Email</Form.Label>
                {fieldsEditable ? (
                  <Form.Control
                    type="email"
                    className={editableBoxWhite}
                    value={emailDraft}
                    onChange={(e) => setEmailDraft(e.target.value)}
                  />
                ) : (
                  <div className={readonlyBoxLight}>
                    {submission.user_email ? (
                      <a href={`mailto:${submission.user_email}`} className="tw-break-words">
                        {submission.user_email}
                      </a>
                    ) : (
                      '–'
                    )}
                  </div>
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Phone</Form.Label>
                {fieldsEditable ? (
                  <Form.Control
                    type="text"
                    className={editableBoxWhite}
                    value={phoneDraft}
                    onChange={(e) => setPhoneDraft(e.target.value)}
                  />
                ) : (
                  <div className={readonlyBoxLight}>
                    {submission.user_phone ? (
                      <a href={`tel:${submission.user_phone.replace(/\s/g, '')}`}>{submission.user_phone}</a>
                    ) : (
                      '–'
                    )}
                  </div>
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Contact Method</Form.Label>
                {fieldsEditable ? (
                  <Form.Control
                    type="text"
                    className={editableBoxWhite}
                    value={contactMethodDraft}
                    onChange={(e) => setContactMethodDraft(e.target.value)}
                  />
                ) : (
                  <Form.Control type="text" readOnly className={readonlyBoxLight} value={submission.contact_method || '–'} />
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Feedback</Form.Label>
                {fieldsEditable ? (
                  <Form.Select
                    size="sm"
                    className={editableBoxWhite}
                    value={feedbackDraft ? 'yes' : 'no'}
                    onChange={(e) => setFeedbackDraft(e.target.value === 'yes')}
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Select>
                ) : (
                  <Form.Control type="text" readOnly className={readonlyBoxLight} value={submission.is_feedback ? 'Yes' : 'No'} />
                )}
              </Form.Group>
            </div>
            <div className="md:tw-w-1/2 tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">No-contact note</Form.Label>
                {fieldsEditable ? (
                  <Form.Control
                    type="text"
                    className={editableBoxWhite}
                    value={noContactNoteDraft}
                    onChange={(e) => setNoContactNoteDraft(e.target.value)}
                  />
                ) : (
                  <Form.Control type="text" readOnly className={readonlyBoxLight} value={submission.noContactNote || '–'} />
                )}
              </Form.Group>
            </div>
            <div className="tw-w-full tw-px-1 tw-mb-2">
              <Form.Group className="tw-mb-0">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Message</Form.Label>
                {fieldsEditable ? (
                  <Form.Control
                    as="textarea"
                    rows={Math.max(3, readOnlyMessageRows(messageDraft))}
                    className={editableBoxWhite}
                    value={messageDraft}
                    onChange={(e) => setMessageDraft(e.target.value)}
                  />
                ) : (
                  <Form.Control
                    as="textarea"
                    readOnly
                    rows={readOnlyMessageRows(submission.message)}
                    className={readonlyBoxLight}
                    value={submission.message ?? '–'}
                    style={{
                      resize: 'none',
                      maxHeight: 'min(40vh, 320px)',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  />
                )}
              </Form.Group>
            </div>
          </div>
          </div>
        </Tab>

        <Tab eventKey="photos" title="Photos">
          <div className="tw-py-2">
            {fieldsEditable && onSaveSubmission && (
              <Form.Group className="tw-mb-3">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Add or replace photo</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  size="sm"
                  className={editableBoxWhite}
                  disabled={saving}
                  onChange={(e) => {
                    const input = e.currentTarget as HTMLInputElement;
                    const f = input.files?.[0] ?? null;
                    setPendingImageFile(f);
                    input.value = '';
                  }}
                />
                <Form.Text className="tw-block tw-mt-1">
                  JPEG, PNG, WebP, or GIF, max 10 MB. Applied when you choose Save on the modal footer.
                </Form.Text>
                {pendingImageFile ? (
                  <div className="tw-text-sm tw-text-content-muted tw-mt-1">
                    Pending upload: <span className="tw-text-content">{pendingImageFile.name}</span>
                  </div>
                ) : null}
              </Form.Group>
            )}
            {wrapPhotoLoading && !pendingPhotoObjectUrl ? (
              <div className="tw-text-content-muted tw-text-sm tw-py-4 tw-text-center">
                <Spinner animation="border" size="sm" className="tw-mr-2" />
                Loading photo…
              </div>
            ) : null}
            {pendingPhotoObjectUrl || (!wrapPhotoLoading && wrapPhotoUrl) ? (
              <div className="tw-text-center">
                <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-2 tw-block tw-text-left">
                  {pendingPhotoObjectUrl ? 'Preview (not saved yet)' : 'Photo'}
                </Form.Label>
                <img
                  src={(pendingPhotoObjectUrl || wrapPhotoUrl) ?? ''}
                  alt="PCR attachment"
                  className="tw-max-w-full tw-h-auto tw-rounded tw-border tw-border-line"
                  style={{ maxHeight: 'min(55vh, 420px)', objectFit: 'contain' }}
                />
              </div>
            ) : null}
            {!wrapPhotoLoading && !wrapPhotoUrl && !pendingPhotoObjectUrl ? (
              <div className="tw-text-center tw-py-4 tw-text-content-muted">
                <i className="bi bi-image tw-text-3xl tw-block tw-mb-2 tw-opacity-50" aria-hidden />
                No photo on file for this PCR.
                {fieldsEditable && onSaveSubmission ? (
                  <p className="tw-text-sm tw-mb-0 tw-mt-2">Use the field above to attach one, then Save.</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </Tab>

        <Tab eventKey="work-id" title="Work ID">
          <div className="tw-py-2">
            {submission.linkedWorkIdentificationId ? (
              <div className="tw-flex tw-flex-col tw-gap-3">
                <div>
                  <Form.Label className="tw-text-content-muted tw-text-sm tw-mb-1">Linked Work ID</Form.Label>
                  <div className={readonlyBoxLight + ' tw-font-semibold'}>
                    {submission.linkedWorkIdentificationId}
                  </div>
                </div>
                {onOpenLinkedWorkId && (
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => onOpenLinkedWorkId(submission)}
                    >
                      <i className="bi bi-box-arrow-up-right tw-mr-2" />
                      Open Work ID
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="tw-text-center tw-py-4 tw-text-content-muted">
                <i className="bi bi-link-45deg tw-text-3xl tw-block tw-mb-2 tw-opacity-50" />
                No Work ID linked to this PCR.
              </div>
            )}
          </div>
        </Tab>
      </Tabs>
    </ResponsiveModal>
  );
};

export default PcrSubmissionDetailModal;
